"use client";

import { createContext, useContext } from "react";

import { EditionJson } from "@/lib/api";

/**
 * The complete set of network calls the booking modal makes.
 *
 * Each member is typed `typeof EditionJson.x` rather than by restating the
 * signature, so a mock cannot drift from the real API: if a real signature
 * changes, every implementation of this interface stops compiling.
 *
 * The context default IS the real implementation, so production renders no
 * provider and behaves exactly as it did before this seam existed. Only the
 * dev harness (`src/app/dev/booking-flow/`) ever supplies a different value.
 */
export interface BookingApi {
  sendVerificationCode: typeof EditionJson.sendVerificationCode;
  verifyVerificationCode: typeof EditionJson.verifyVerificationCode;
  getAppointmentHistory: typeof EditionJson.getAppointmentHistory;
  /**
   * Not a modal call: the location page's slot grid (`MemberOpenings`) fetches
   * this itself. It lives here so the dev harness can mount the REAL booking
   * UI — date picker, service picker, stylist cards, slot grid — instead of
   * synthetic buttons.
   */
  getLocationOpeningsByServices: typeof EditionJson.getLocationOpeningsByServices;
  /**
   * The stylist page's slot grid and its empty state. Like
   * `getLocationOpeningsByServices` these are not modal calls — they belong to
   * the booking UI the harness mounts, and go through the seam so it can serve
   * them fixtures.
   */
  getOpeningsByDuration: typeof EditionJson.getOpeningsByDuration;
  getUserNextOpeningsByDuration: typeof EditionJson.getUserNextOpeningsByDuration;
  /**
   * The stylist card's info panel (the ⓘ button on the location page). Without
   * these the harness's "nothing reaches the real API" promise is false for
   * that one control.
   */
  getStylistExperience: typeof EditionJson.getStylistExperience;
  getUser: typeof EditionJson.getUser;
  /**
   * Re-prices selected services against the schedule/member they'll actually
   * be booked with. A stylist's canonical menu can disagree with a
   * cross-business slot's real price or payment requirement (Task 17); one
   * modal-level controller uses this result for review, routing and payment.
   * The call is read-only, but its result is authoritative for booking flow.
   */
  getServicePricing: typeof EditionJson.getServicePricing;
  getVerifiedAppointmentHistory: typeof EditionJson.getVerifiedAppointmentHistory;
  isCustomerBookingBlocked: typeof EditionJson.isCustomerBookingBlocked;
  createPaymentMethod: typeof EditionJson.createPaymentMethod;
  createAppointment: typeof EditionJson.createAppointment;
  rescheduleAppointment: typeof EditionJson.rescheduleAppointment;
  /**
   * The consultation ("Service Request") form reached from a stylist card when
   * a selected service has `hasConsultation`. A real CRM write.
   */
  sendServiceRequest: typeof EditionJson.sendServiceRequest;
  /**
   * The consultation form's photo uploader. Not an EditionJson call — the
   * component used to POST straight to `/api/upload`, which writes to Vercel
   * Blob. Routing it through the seam is what lets the harness exercise the
   * upload UI (previews, removal, the 8-file cap) without storing anything.
   *
   * Resolves the shape `/api/upload` returns, because the form reads
   * `file.response.url` to build its payload.
   */
  uploadServiceRequestPhoto: (file: File) => Promise<{ url: string }>;
}

// Frozen: this is the context default every production render falls back to
// (no provider mounted). Freezing makes an accidental mutation a thrown
// TypeError in dev instead of a silent behavior change shared by every
// consumer of the real API.
export const REAL_BOOKING_API: BookingApi = Object.freeze({
  sendVerificationCode: EditionJson.sendVerificationCode,
  verifyVerificationCode: EditionJson.verifyVerificationCode,
  getAppointmentHistory: EditionJson.getAppointmentHistory,
  getLocationOpeningsByServices: EditionJson.getLocationOpeningsByServices,
  getOpeningsByDuration: EditionJson.getOpeningsByDuration,
  getUserNextOpeningsByDuration: EditionJson.getUserNextOpeningsByDuration,
  getStylistExperience: EditionJson.getStylistExperience,
  getUser: EditionJson.getUser,
  getServicePricing: EditionJson.getServicePricing,
  getVerifiedAppointmentHistory: EditionJson.getVerifiedAppointmentHistory,
  isCustomerBookingBlocked: EditionJson.isCustomerBookingBlocked,
  createPaymentMethod: EditionJson.createPaymentMethod,
  createAppointment: EditionJson.createAppointment,
  rescheduleAppointment: EditionJson.rescheduleAppointment,
  sendServiceRequest: EditionJson.sendServiceRequest,
  uploadServiceRequestPhoto: async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await fetch("/api/upload", {
      method: "POST",
      body: formData,
    });
    if (!response.ok) {
      throw new Error(await response.text());
    }
    return response.json();
  },
});

const BookingApiContext = createContext<BookingApi>(REAL_BOOKING_API);

/**
 * The `value` passed here must be referentially stable across renders (e.g.
 * memoized or constructed once via `useState`'s lazy initializer — see
 * `BookingFlowHarnessClient.tsx`). `RescheduleMain.tsx` has an effect that
 * depends on `bookingApi` and fires a network call on change; a new object
 * literal on every render would re-fire that effect in a loop.
 */
export const BookingApiProvider = BookingApiContext.Provider;

export const useBookingApi = (): BookingApi => useContext(BookingApiContext);
